'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from 'next/image';
import { usePathname, useRouter } from 'next/navigation';
import ThemeToggle from '../ui/theme-toggle';
import LanguageSelector from '../ui/language-selector';
import { useLanguage } from '@/app/context/language-context';
import { useTheme } from '@/app/components/theme-provider';
import { Menu, X, ChevronDown } from 'lucide-react';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const pathname = usePathname();
  const router = useRouter();
  const currentPage = pathname === '/' ? 'home' : 'other';

  // Check if we're on the home page
  const isHomePage = pathname === '/';

  // Services submenu items
  const servicesSubmenu = [
    { name: 'Property Care', href: '/services/property-care' },
    { name: 'Golden Visa', href: '/services/golden-visa' },
    { name: 'Sell Property', href: '/services/sell-property' },
    { name: 'Mortgage', href: '/services/mortgage-consultancy' },
  ];

  // Properties submenu items
  const propertiesSubmenu = [
    { name: t('buy'), href: '/properties/buy' },
    { name: t('rent'), href: '/properties/rent' },
    { name: t('offPlan'), href: '/properties/off-plan' },
  ];

  // Split links into left and right sections
  const leftLinks = [
    {
      name: t('properties'),
      url: '/properties/buy',
      activePrefix: '/properties',
      hasSubmenu: true,
      submenu: propertiesSubmenu,
    },
    { name: t('projects'), url: '/projects' },
    { name: t('aboutUs'), url: '/about' },
    { name: t('newsArticles'), url: '/articles' },
  ];

  const rightLinks = [
    { name: t('blog'), url: '/blog' },
    { name: t('agent'), url: '/agents' },
    { name: t('contactUs'), url: '/contact-us' }, // Changed from Contact Us
    {
      name: t('services'),
      url: '/services',
      hasSubmenu: true,
      submenu: servicesSubmenu,
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Close the mobile menu only if scrolled more than 100px
      if (isMobileMenuOpen && window.scrollY > 100) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen]);

  // Scroll to top when navigating
  // const handleNavigation = (href) => {
  //   router.push(href, undefined, { scroll: false }); // prevent default scroll
  //   setIsMobileMenuOpen(false);
  //   setIsServicesDropdownOpen(false);

  //   // smooth scroll after navigation
  //   setTimeout(() => {
  //     window.scrollTo({ top: 0, behavior: 'smooth' });
  //   }, 100);
  // };

  const handleNavigation = (href) => {
    router.push(href, undefined, { scroll: true }); // instant scroll reset
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
  };

  const getLogoSrc = () => {
    // Priority: mobile menu open
    if (isMobileMenuOpen) {
      return theme === 'light'
        ? language === 'zh'
          ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_black_ch_no_bg.webp'
          : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_black_eng_no_bg.webp'
        : language === 'zh'
          ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_white_ch_no_bg.webp'
          : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_white_eng_no_bg.webp';
    }

    // Dark theme → always white logo
    if (theme === 'dark') {
      return language === 'zh'
        ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_white_ch_no_bg.webp'
        : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_white_eng_no_bg.webp';
    }

    // Light theme cases
    if (theme === 'light') {
      if (currentPage === 'home') {
        // On homepage, use black logo only if scrolled
        return isScrolled
          ? language === 'zh'
            ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_black_ch_no_bg.webp'
            : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_black_eng_no_bg.webp'
          : language === 'zh'
            ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_white_ch_no_bg.webp'
            : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_white_eng_no_bg.webp';
      } else {
        // On all other pages → always black logo
        return language === 'zh'
          ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_black_ch_no_bg.webp'
          : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_black_eng_no_bg.webp';
      }
    }

    // Fallback (shouldn't reach here)
    return language === 'zh'
      ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_white_ch_no_bg.webp'
      : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_white_eng_no_bg.webp';
  };

  // Determine text color based on page, scroll position, and theme
  const getTextColor = () => {
    if (isScrolled) {
      return 'text-gray-700 hover:text-primarycolor dark:text-gray-200';
    }

    if (isHomePage) {
      return 'text-white hover:text-primarycolor dark:hover:text-secondarycolor';
    }

    return theme === 'light'
      ? 'text-gray-700 hover:text-primarycolor'
      : 'text-white hover:text-primarycolor';
  };

  // Determine mobile menu button color
  const getMobileButtonColor = () => {
    if (isMobileMenuOpen) {
      return theme === 'light' ? 'text-black' : 'text-white';
    }

    if (isScrolled) {
      return 'text-gray-700 hover:text-primarycolor dark:text-gray-200';
    }

    if (isHomePage) {
      return 'text-white hover:text-primarycolor';
    }

    return theme === 'light'
      ? 'text-gray-700 hover:text-primarycolor'
      : 'text-white hover:text-primarycolor';
  };

  const isActiveLink = (url) => pathname.startsWith(url);

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isMobileMenuOpen
          ? theme === 'light'
            ? 'bg-white shadow-lg'
            : 'bg-gray-900 shadow-lg'
          : isScrolled
            ? 'bg-white shadow-lg dark:bg-gray-900'
            : 'bg-transparent'
      }`}
    >
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
        <div className='flex items-center justify-between h-20'>
          {/* Left side navigation */}
          <div className='flex items-center lg:justify-start justify-center'>
            {/* Mobile menu button */}
            <div className='lg:hidden mr-4'>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`p-2 rounded-md ${getMobileButtonColor()}`}
                aria-label='Toggle mobile menu'
              >
                {isMobileMenuOpen ? (
                  <X className='h-6 w-6' />
                ) : (
                  <Menu className='h-6 w-6' />
                )}
              </button>
            </div>

            <nav className='hidden lg:flex space-x-4 sm:space-x-6'>
              {leftLinks.map((link) =>
                link.hasSubmenu ? (
                  <div
                    key={link.name}
                    className='relative ml-4'
                    onMouseEnter={() => setOpenDropdown(link.name)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      onClick={() => handleNavigation(link.url)}
                      className={`flex items-center text-sm/10 font-semibold transition-colors cursor-pointer ${getTextColor()} ${
                        isActiveLink(link.activePrefix || link.url)
                          ? 'border-b-2 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor'
                          : ''
                      }`}
                    >
                      {link.name.toUpperCase()}
                      <ChevronDown className='ml-1 h-4 w-4' />
                    </button>

                    <AnimatePresence>
                      {openDropdown === link.name && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className='absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2'
                        >
                          {link.submenu.map((submenuItem) => (
                            <button
                              key={submenuItem.name}
                              onClick={() => handleNavigation(submenuItem.href)}
                              className='block w-full text-left px-4 py-2 text-sm/12 font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primarycolor transition-colors'
                            >
                              {submenuItem.name}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <button
                    key={link.name}
                    onClick={() => handleNavigation(link.url)}
                    className={`text-sm/10 ml-4 font-semibold transition-colors cursor-pointer ${getTextColor()} ${
                      isActiveLink(link.url)
                        ? 'border-b-2 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor'
                        : ''
                    }`}
                  >
                    {link.name.toUpperCase()}
                  </button>
                )
              )}
            </nav>
          </div>

          {/* Logo - centered */}
          <div className='flex justify-center'>
            <button
              onClick={() => handleNavigation('/')}
              className='flex-shrink-0'
            >
              <div className='relative h-10 w-[115px]'>
                <Image
                  src={getLogoSrc() || '/placeholder.svg'}
                  alt='X-Space Real Estate'
                  fill
                  priority
                  className='object-contain'
                  sizes='(max-width: 768px) 80px, 115px'
                />
              </div>
            </button>
          </div>

          {/* Right side navigation */}
          <div className='flex items-center justify-end'>
            <nav className='hidden lg:flex items-center space-x-8 '>
              {rightLinks.map((link) => (
                <div key={link.name} className='relative ml-4'>
                  {link.hasSubmenu ? (
                    <div
                      className='relative'
                      onMouseEnter={() => setOpenDropdown(link.name)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <button
                        onClick={() => handleNavigation(link.url)}
                        className={`flex items-center text-sm/10 font-semibold transition-colors cursor-pointer ${getTextColor()} ${
                          isActiveLink(link.activePrefix || link.url)
                            ? 'border-b-2 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor'
                            : ''
                        }`}
                      >
                        {link.name.toUpperCase()}
                        <ChevronDown className='ml-1 h-4 w-4' />
                      </button>

                      <AnimatePresence>
                        {openDropdown === link.name && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className='absolute top-full left-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2'
                          >
                            {link.submenu.map((submenuItem) => (
                              <button
                                key={submenuItem.name}
                                onClick={() =>
                                  handleNavigation(submenuItem.href)
                                }
                                className='block w-full text-left px-4 py-2 text-sm/12 font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primarycolor transition-colors'
                              >
                                {submenuItem.name}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNavigation(link.url)}
                      className={`text-sm/10 font-semibold transition-colors cursor-pointer ${getTextColor()} ${
                        isActiveLink(link.url)
                          ? 'border-b-2 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor'
                          : ''
                      }`}
                    >
                      {link.name.toUpperCase()}
                    </button>
                  )}
                </div>
              ))}
              <div className='flex items-center space-x-2'>
                <LanguageSelector currentPage={currentPage} />
                <ThemeToggle />
              </div>
            </nav>

            {/* Mobile language and theme toggles */}
            <div className='flex items-center lg:hidden'>
              <LanguageSelector currentPage={currentPage} />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className='lg:hidden bg-white dark:bg-gray-900 shadow-lg'
          >
            <div className='px-4 pt-2 pb-6 space-y-2'>
              {/* Left links */}
              {leftLinks.map((link) => (
                <div key={link.name}>
                  {link.hasSubmenu ? (
                    <div>
                      <button
                        onClick={() => handleNavigation(link.url)}
                        className={`block w-full text-left py-2 font-semibold transition-colors ${
                          isActiveLink(link.activePrefix || link.url)
                            ? 'border-l-4 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor pl-2'
                            : 'text-gray-700 hover:text-primarycolor dark:text-gray-200'
                        }`}
                      >
                        {link.name.toUpperCase()}
                      </button>
                      {/* Mobile submenu */}
                      <div className='ml-4 mt-1 space-y-1'>
                        {link.submenu.map((submenuItem) => (
                          <button
                            key={submenuItem.name}
                            onClick={() => handleNavigation(submenuItem.href)}
                            className='block w-full text-left py-1 text-sm text-gray-600 hover:text-primarycolor dark:text-gray-300 dark:hover:text-secondarycolor transition-colors'
                          >
                            {submenuItem.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNavigation(link.url)}
                      className={`block w-full text-left py-2 font-semibold transition-colors ${
                        isActiveLink(link.url)
                          ? 'border-l-4 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor pl-2'
                          : 'text-gray-700 hover:text-primarycolor dark:text-gray-200'
                      }`}
                    >
                      {link.name.toUpperCase()}
                    </button>
                  )}
                </div>
              ))}

              {/* Right links */}
              {rightLinks.map((link) => (
                <div key={link.name}>
                  {link.hasSubmenu ? (
                    <div>
                      <button
                        onClick={() => handleNavigation(link.url)}
                        className={`block w-full text-left py-2 font-semibold transition-colors ${
                          isActiveLink(link.url)
                            ? 'border-l-4 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor pl-2'
                            : 'text-gray-700 hover:text-primarycolor dark:text-gray-200'
                        }`}
                      >
                        {link.name.toUpperCase()}
                      </button>
                      {/* Mobile submenu */}
                      <div className='ml-4 mt-1 space-y-1'>
                        {link.submenu.map((submenuItem) => (
                          <button
                            key={submenuItem.name}
                            onClick={() => handleNavigation(submenuItem.href)}
                            className='block w-full text-left py-1 text-sm text-gray-600 hover:text-primarycolor dark:text-gray-300 dark:hover:text-secondarycolor transition-colors'
                          >
                            {submenuItem.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleNavigation(link.url)}
                      className={`block w-full text-left py-2 font-semibold transition-colors ${
                        isActiveLink(link.url)
                          ? 'border-l-4 border-primarycolor dark:border-secondarycolor text-primarycolor dark:text-secondarycolor pl-2'
                          : 'text-gray-700 hover:text-primarycolor dark:text-gray-200'
                      }`}
                    >
                      {link.name.toUpperCase()}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
